/* eslint-disable indent */
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
    InputAdornment,
    CircularProgress
} from '@mui/material';
import i18n from '../../i18';
import {
    EmptyWalletTransparentBulkIcon,
    ProfileCircleBulkIcon
} from '../../icons-lib';
import AssociatedBlockComponent from '../../components/associated-block/AssociatedBlockComponent';
import { fCurrency } from '../../utils/formatNumber';
import SearchBox from '../../search-box/SearchBox';

const TopUpDialog = ({
    open,
    onClose,
    onTopUp,
    employeeData,
    context,
    userInfo
}) => {
    const [step, setStep] = useState(1);
    const [amount, setAmount] = useState('');
    const [fromWallet, setFromWallet] = useState(null);
    const [fromWalletId, setFromWalletId] = useState(null);
    const [formError, setFormError] = useState('');
    const [exchangeRate, setExchangeRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);
    // no rateError needed
    const { server, controllersData } = context.getState();

    const isSmeAdmin = userInfo?.role?.systemRole === 'sme_admin';

    // Get pre-loaded SME companies data (same as create/edit forms)
    const smeCompanies = controllersData?.smeCompanies || [];
    const smeCompaniesTotalCount = controllersData?.smeCompaniesTotalCount || 0;

    useEffect(() => {
        const fetchSmeCompany = async () => {
            if (open && isSmeAdmin) {
                const company = await server.smeCompanies.getById(
                    userInfo.smeCompanyId
                );
                setFromWallet(company);
                setFromWalletId(company?.id);
            }
        };

        if (userInfo) {
            fetchSmeCompany();
        }
    }, [open, isSmeAdmin, server.smeCompanies, userInfo]);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            // Reset to initial state when dialog opens
            setStep(1);
            setAmount('');
            setFormError('');
            // Reset wallet selection for non-SME admins
            if (!isSmeAdmin) {
                setFromWallet(null);
                setFromWalletId(null);
            }
        }
    }, [open, isSmeAdmin]);

    // reset error when user changes input
    useEffect(() => {
        if (formError) {
            setFormError('');
        }
    }, [amount, fromWalletId]);

    // fetch exchange rate each time dialog opens
    useEffect(() => {
        const fetchRate = async () => {
            if (!open) return;
            try {
                setRateLoading(true);
                const userCurrency = localStorage.currency || 'CHF';
                const resp = await server.currencyExchangeRates.getAll();
                const pair = resp?.data?.find(
                    (r) =>
                        r.currencyExchangePair?.fromCurrency?.shortName ===
                            'XAG' &&
                        r.currencyExchangePair?.toCurrency?.shortName ===
                            userCurrency
                );
                setExchangeRate(pair ? 1 / parseFloat(pair.rate) : null);
            } catch (e) {
                console.error('exchange rate fetch failed', e);
                setExchangeRate(null);
            } finally {
                setRateLoading(false);
            }
        };
        fetchRate();
    }, [open, server.currencyExchangeRates]);

    const validateInputs = () => {
        const amt = parseFloat(amount);
        if (!fromWallet) {
            setFormError(i18n.t('errors.selectWallet'));
            return false;
        }
        if (isNaN(amt) || amt <= 0) {
            setFormError(i18n.t('errors.enterAmount'));
            return false;
        }
        const convertedAmount = amt * exchangeRate;
        const balance = parseFloat(fromWallet?.wallet?.currentBalance || 0);
        if (convertedAmount > balance) {
            setFormError(i18n.t('errors.insufficientBalance'));
            return false;
        }
        setFormError('');
        return true;
    };

    const handleProceed = () => {
        if (validateInputs()) {
            setStep(2);
        }
    };

    const handleConfirm = async () => {
        if (!exchangeRate) {
            // should not happen; safeguard
            return;
        }
        const amtUserCurrency = parseFloat(amount);
        const amt = amtUserCurrency * exchangeRate;
        try {
            await onTopUp({
                employeeId: employeeData.id,
                employeeName: employeeData.firstName,
                amount: amt,
                fromWallet,
                toWalletId: employeeData.wallet?.id
            });
            onClose();
        } catch (error) {
            console.error('Top-up failed:', error);
        }
    };

    if (!userInfo) {
        return null;
    }

    const renderStepOne = () => (
        <>
            <DialogTitle>
                {i18n.t('dialogs.topUp.title', {
                    employeeName: employeeData?.firstName || ''
                })}
            </DialogTitle>
            <DialogContent>
                {/* From Wallet Section */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1}
                >
                    <Typography variant="h6">
                        {i18n.t('dialogs.topUp.fromWallet')}
                    </Typography>
                    {fromWallet?.wallet?.walletNumber && (
                        <Typography variant="subtitle1" color="textSecondary">
                            {fromWallet.wallet.walletNumber}
                        </Typography>
                    )}
                </Box>

                {fromWallet ? (
                    <AssociatedBlockComponent
                        icon={<EmptyWalletTransparentBulkIcon />}
                        primaryTitle={i18n.t('columns.account')}
                        primaryContent={fromWallet.name}
                        secondaryTitle={i18n.t('columns.balance')}
                        secondaryContent={`${fCurrency(
                            fromWallet?.wallet?.currentBalance || 0
                        )} SILbar`}
                        hideSecondaryLabel={false}
                        action={{
                            label: i18n.t('buttons.change'),
                            onClick: () => {
                                setFromWallet(null);
                                setFromWalletId(null);
                            }
                        }}
                    />
                ) : (
                    !isSmeAdmin && (
                        <SearchBox
                            serverSearch
                            searchFields={['name', 'email']}
                            searchController="smeCompanies"
                            handleChange={(value) => {
                                if (value) {
                                    const company = smeCompanies.find(
                                        (c) => c.id === value
                                    );
                                    if (company) {
                                        setFromWallet(company);
                                        setFromWalletId(null);
                                    }
                                }
                            }}
                            value={fromWalletId}
                            multiple={false}
                            context={context}
                            icon={<EmptyWalletTransparentBulkIcon />}
                            translatedLabel={i18n.t('buttons.chooseSmeAccount')}
                            translatedSearchLabel={i18n.t(
                                'pages.smeCompanies.title'
                            )}
                            primaryTitle={i18n.t('columns.smeName')}
                            secondaryTitle={i18n.t('columns.email')}
                            primaryKey="name"
                            secondaryKey="email"
                            items={smeCompanies}
                            totalItemsCount={smeCompaniesTotalCount}
                            saveFullData={false}
                        />
                    )
                )}

                {/* To Wallet Section */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mt={3}
                    mb={1}
                >
                    <Typography variant="h6">
                        {i18n.t('dialogs.topUp.toWallet')}
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary">
                        {employeeData?.wallet?.walletNumber || ''}
                    </Typography>
                </Box>
                <AssociatedBlockComponent
                    icon={<ProfileCircleBulkIcon />}
                    primaryTitle={i18n.t('columns.employeeName')}
                    primaryContent={employeeData?.firstName || ''}
                    secondaryTitle={i18n.t('columns.email')}
                    secondaryContent={employeeData?.email || ''}
                    hideSecondaryLabel={false}
                />

                {/* Amount input */}
                <Box mt={3}>
                    <TextField
                        fullWidth
                        label={i18n.t('dialogs.topUp.amountLabel')}
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        inputProps={{ step: '0.01' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: '#BA9B61'
                                        }}
                                    >
                                        {localStorage.currency || 'CHF'}
                                    </Typography>
                                </InputAdornment>
                            )
                        }}
                    />
                    {/* SILbar equivalent */}
                    {amount && !isNaN(parseFloat(amount)) && (
                        <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                            {rateLoading ? (
                                <CircularProgress size={12} />
                            ) : exchangeRate ? (
                                <Typography
                                    variant="caption"
                                    sx={{ color: '#98A2B3' }}
                                >
                                    {`≈ ${(
                                        parseFloat(amount) * exchangeRate
                                    ).toFixed(2)} SILbar`}
                                </Typography>
                            ) : null}
                        </Box>
                    )}
                </Box>
                {formError && (
                    <Typography color="error" sx={{ mt: 1, ml: 2 }}>
                        {formError}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{i18n.t('buttons.cancel')}</Button>
                <Button onClick={handleProceed} variant="contained">
                    {i18n.t('buttons.proceed')}
                </Button>
            </DialogActions>
        </>
    );

    const renderStepTwo = () => (
        <>
            <DialogTitle>{i18n.t('dialogs.confirmationTitle')}</DialogTitle>
            <DialogContent>
                {/* Chips row */}
                <Box display="flex" gap={2} mb={3}>
                    <AssociatedBlockComponent
                        icon={<EmptyWalletTransparentBulkIcon />}
                        primaryTitle={i18n.t('dialogs.topUp.fromWallet')}
                        primaryContent={fromWallet?.name || ''}
                        secondaryTitle={i18n.t('columns.walletNumber')}
                        secondaryContent={
                            fromWallet?.wallet?.walletNumber || ''
                        }
                        hideSecondaryLabel={false}
                    />
                    <AssociatedBlockComponent
                        primaryTitle={i18n.t('dialogs.topUp.amountLabel')}
                        primaryContent={`${fCurrency(amount)} ${
                            localStorage.currency || 'CHF'
                        }${
                            exchangeRate
                                ? ` ≈ ${fCurrency(
                                      parseFloat(amount) * exchangeRate
                                  )} SILbar`
                                : ''
                        }`}
                        hideSecondaryLabel={true}
                    />
                </Box>

                <Typography variant="h6" gutterBottom>
                    {i18n.t('dialogs.topUp.toWallet')}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {i18n.t('columns.walletNumber')}
                                </TableCell>
                                <TableCell>
                                    {i18n.t('columns.employeeName')}
                                </TableCell>
                                <TableCell>{i18n.t('columns.email')}</TableCell>
                                <TableCell align="right">
                                    {i18n.t('dialogs.topUp.amountLabel')}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow hover>
                                <TableCell>
                                    {employeeData?.wallet?.walletNumber || ''}
                                </TableCell>
                                <TableCell>
                                    {employeeData?.firstName || ''}
                                </TableCell>
                                <TableCell>
                                    {employeeData?.email || ''}
                                </TableCell>
                                <TableCell align="right">
                                    {`${fCurrency(amount)} ${
                                        localStorage.currency || 'CHF'
                                    }${
                                        exchangeRate
                                            ? ` ≈ ${fCurrency(
                                                  parseFloat(amount) *
                                                      exchangeRate
                                              )} SILbar`
                                            : ''
                                    }`}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setStep(1)}>
                    {i18n.t('buttons.back')}
                </Button>
                <Button onClick={handleConfirm} variant="contained">
                    {i18n.t('buttons.confirmTopUp')}
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            {step === 1 ? renderStepOne() : renderStepTwo()}
        </Dialog>
    );
};

export default TopUpDialog;
